import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Checkbox, Grid, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, Biotech as LabIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadLabCategoriesbd, getLabInsurances,
  updateInsuredLab, deleteInsuredLab, saveNewLab,
  bulkApplyLabInsurance, moveLabToInsurancedb
} from '../BaseURL';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

/* ─────────────────────────────────────────────────────────────
   Shared theme tokens — every sub-component pulls colors from
   here so the page renders cleanly on both default and black themes.
───────────────────────────────────────────────────────────── */
const useLabTokens = () => {
  const theme = useTheme();
  return useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      isDark,
      pageBg:       isDark ? '#000000' : '#F4F6FA',
      surface:      isDark ? '#0E0E14' : '#FFFFFF',
      surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
      fieldBg:      isDark ? '#14141C' : '#FFFFFF',
      borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
      borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
      text:         isDark ? '#FFFFFF' : '#0F172A',
      textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
      textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
      rowOdd:       isDark ? 'rgba(255,255,255,0.02)' : '#F9FAFF',
      rowEven:      isDark ? 'transparent' : '#FFFFFF',
      rowHover:     isDark ? 'rgba(17,153,142,0.10)' : 'rgba(17,153,142,0.05)',
      rowSelected:  isDark ? 'rgba(17,153,142,0.18)' : 'rgba(17,153,142,0.12)',
      cardShadow:   isDark
        ? '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.35)'
        : '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
      accent: {
        teal:    '#11998e',  // brand
        primary: '#6272ea',
        success: '#10b981',
        danger:  '#ef4444',
        warning: '#f59e0b',
        info:    '#0ea5e9',
      }
    };
  }, [theme.palette.mode]);
};

/* ── Editable Row ───────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const t = useLabTokens();
  const cellInputSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.78rem',
      backgroundColor: t.fieldBg,
      '& input': { py: '4px', px: '8px', color: t.text },
      '& fieldset': { borderColor: t.borderSubtle },
      '&:hover fieldset': { borderColor: t.accent.teal },
      '&.Mui-focused fieldset': { borderColor: t.accent.teal },
    },
  };
  const tdSx = { borderBottom: `1px solid ${t.borderSubtle}`, py: 0.75, px: 1, color: t.text };
  const rowId = `${row.trans_code}-${row.status}`;
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)':  { background: selected ? t.rowSelected : t.rowOdd },
      '&:nth-of-type(even)': { background: selected ? t.rowSelected : t.rowEven },
      '&:hover': { background: selected ? t.rowSelected : t.rowHover },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox size="small" checked={selected} onChange={() => onSelectToggle(rowId)}
          sx={{ color: t.accent.teal, '&.Mui-checked': { color: t.accent.teal }, p: 0.5 }} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: t.textDim, fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 150 }}>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: t.accent.teal }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.cost ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('cost', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.toup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('toup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.unin ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('unin', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.G_DRG ?? ''} onChange={e => onChange('G_DRG', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{
              fontSize: '0.7rem', px: 1.5, py: 0.5, fontWeight: 600, textTransform: 'none', borderRadius: 1,
              background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
              boxShadow: `0 2px 8px ${alpha(t.accent.teal, 0.3)}`,
              '&:hover': { boxShadow: `0 4px 14px ${alpha(t.accent.teal, 0.45)}` },
              '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
            }}>
            {updating ? 'Saving…' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{
              fontSize: '0.7rem', px: 1.5, py: 0.5, fontWeight: 600, textTransform: 'none', borderRadius: 1,
              background: `linear-gradient(135deg, ${t.accent.danger} 0%, ${alpha(t.accent.danger, 0.82)} 100%)`,
              boxShadow: `0 2px 8px ${alpha(t.accent.danger, 0.3)}`,
              '&:hover': { boxShadow: `0 4px 14px ${alpha(t.accent.danger, 0.45)}` },
              '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
            }}>
            {deleting ? '…' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};




const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses, selectedRows }) => {
  const t = useLabTokens();
  const cellInputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: t.fieldBg, borderRadius: 1.5,
      '& fieldset': { borderColor: t.borderSubtle },
      '&:hover fieldset': { borderColor: t.accent.teal },
      '&.Mui-focused fieldset': { borderColor: t.accent.teal },
    },
  };
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [editCost, setEditCost] = useState('');
  const [editToup, setEditToup] = useState('');
  const [editUnin, setEditUnin] = useState('');
  const [editDRG, setEditDRG] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

 useEffect(() => {
    if (open && sourceRow) {
      // Pre-check all insurances matching the selected rows
      const preChecked = {};
      if (selectedRows?.length > 0) {
        selectedRows.forEach(row => {
          const match = insuranceProviders.find(p => p.name === row.status);
          if (match) preChecked[match.id] = true;
        });
      }
      setSelectedInsurances(preChecked);
      setInsuranceSearch('');
    }
  }, [open, sourceRow, selectedRows, insuranceProviders]);

  const handleInsuranceToggle = (id) => setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
  };
  const handleClose = () => { setSelectedInsurances({}); setInsuranceSearch(''); onClose(); };
  const getProviderName = (id) => insuranceProviders.find(p => String(p.id) === String(id))?.name || '';

  const handleUpdate = async () => {
    if (selectedCount === 0) { await Swal.fire({ title: 'No Selection', text: 'Select at least one insurance.', icon: 'warning', confirmButtonColor: '#11998e' }); return; }
    if (editCost === '' || editToup === '' || editUnin === '') { await Swal.fire({ title: 'Validation', text: 'Fill in all price fields.', icon: 'warning', confirmButtonColor: '#11998e' }); return; }
    const confirm = await Swal.fire({
      title: 'Apply Pricing?', html: `Apply new values to <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}?`,
      icon: 'question', showCancelButton: true, confirmButtonColor: '#11998e', cancelButtonColor: '#9e9e9e', confirmButtonText: 'Yes, Apply'
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => ({
        trans_code: sourceRow.trans_code, lab: sourceRow.lab,
        cost: parseFloat(editCost), toup: parseFloat(editToup),
        unin: parseFloat(editUnin), G_DRG: editDRG.trim(),
        status: getProviderName(id),
      }));
      const response = await axios.post(bulkApplyLabInsurance, { rows });
      if (response.data.success) {
        await Swal.fire({ title: 'Applied!', html: `<b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''} updated.`, icon: 'success', timer: 2500, showConfirmButton: false, timerProgressBar: true });
        if (onSaved) onSaved(); handleClose();
      } else throw new Error(response.data.message || 'Apply failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (selectedCount === 0) { await Swal.fire({ title: 'No Selection', text: 'Select at least one insurance to delete.', icon: 'warning', confirmButtonColor: '#11998e' }); return; }
    const confirm = await Swal.fire({
      title: 'Delete Records?', html: `Delete pricing for <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}?`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e', confirmButtonText: 'Yes, Delete'
    });
    if (!confirm.isConfirmed) return;
    setDeleting(true);
    try {
      const promises = selectedInsuranceIds.map(id =>
        axios.post(deleteInsuredLab, { trans_code: sourceRow.trans_code, lab: sourceRow.lab, status: getProviderName(id) })
      );
      await Promise.all(promises);
      await Swal.fire({ title: 'Deleted!', html: `<b>${selectedCount}</b> record${selectedCount !== 1 ? 's' : ''} deleted.`, icon: 'success', timer: 2000, showConfirmButton: false, timerProgressBar: true });
      if (onSaved) onSaved(); handleClose();
    } catch (err) { await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setDeleting(false); }
  };

  const filteredProviders = insuranceProviders.filter(i => i.name?.toLowerCase().includes(insuranceSearch.toLowerCase()));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: {
        borderRadius: 3, overflow: 'hidden',
        backgroundColor: t.pageBg, backgroundImage: 'none',
        border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
        boxShadow: t.isDark ? '0 24px 64px rgba(0,0,0,0.7)' : '0 24px 64px rgba(15,23,42,0.18)',
      } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.78)} 100%)`,
        color: 'white', p: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            backgroundColor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}>
            <CopyIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{sourceRow.lab}</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.18)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
          position: 'relative', zIndex: 1
        }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: t.pageBg }}>
        <Grid container>

          {/* LEFT: Edit fields + action buttons */}
          <Grid item xs={12} md={5} sx={{
            p: 3,
            borderRight: { md: `1px solid ${t.borderSubtle}` },
            backgroundColor: t.surfaceAlt,
            display: 'flex', flexDirection: 'column', gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.teal, boxShadow: `0 0 10px ${alpha(t.accent.teal, 0.5)}` }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.74rem' }}>Edit Values</Typography>
                <Typography variant="caption" sx={{ color: t.textMuted }}>Applied to every selected insurance on the right</Typography>
              </Box>
            </Box>
            <TextField fullWidth size="small" label="Tariff GH¢" type="number"
              value={editCost} onChange={e => setEditCost(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="Top-up GH¢" type="number"
              value={editToup} onChange={e => setEditToup(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="Uninsured GH¢" type="number"
              value={editUnin} onChange={e => setEditUnin(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="DRG Code (optional)"
              value={editDRG} onChange={e => setEditDRG(e.target.value)}
              placeholder="e.g., INVE122" sx={cellInputSx} />
            <Divider sx={{ borderColor: t.borderSubtle }} />
            <Button fullWidth variant="contained" onClick={handleUpdate}
              disabled={saving || deleting || selectedCount === 0}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                color: 'white', fontWeight: 700, textTransform: 'none', py: 1, borderRadius: 1.5,
                boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.3)}`,
                '&:hover': { boxShadow: `0 6px 18px ${alpha(t.accent.teal, 0.45)}` },
                '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
              }}>
              {saving ? 'Updating…' : `Update ${selectedCount > 0 ? `${selectedCount} ` : ''}Insurance${selectedCount !== 1 ? 's' : ''}`}
            </Button>
            <Button fullWidth variant="contained" onClick={handleDelete}
              disabled={deleting || saving || selectedCount === 0}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              sx={{
                background: `linear-gradient(135deg, ${t.accent.danger} 0%, ${alpha(t.accent.danger, 0.82)} 100%)`,
                color: 'white', fontWeight: 700, textTransform: 'none', py: 1, borderRadius: 1.5,
                boxShadow: `0 4px 12px ${alpha(t.accent.danger, 0.3)}`,
                '&:hover': { boxShadow: `0 6px 18px ${alpha(t.accent.danger, 0.45)}` },
                '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
              }}>
              {deleting ? 'Deleting…' : `Delete ${selectedCount > 0 ? `${selectedCount} ` : ''}Insurance${selectedCount !== 1 ? 's' : ''}`}
            </Button>
            {selectedCount === 0 && <Typography variant="caption" sx={{ color: t.accent.danger, textAlign: 'center', fontSize: '0.7rem' }}>Select at least one insurance on the right</Typography>}
          </Grid>

          {/* RIGHT: All insurance providers */}
          <Grid item xs={12} md={7} sx={{ p: 3, display: 'flex', flexDirection: 'column', backgroundColor: t.surface }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.primary, boxShadow: `0 0 10px ${alpha(t.accent.primary, 0.5)}` }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.74rem' }}>
                  Select Insurances
                </Typography>
              </Box>
              {selectedCount > 0 && <Chip size="small" label={`${selectedCount} selected`} sx={{
                backgroundColor: alpha(t.accent.success, t.isDark ? 0.18 : 0.14),
                color: t.accent.success,
                fontWeight: 700, fontSize: '0.7rem',
                border: `1px solid ${alpha(t.accent.success, 0.3)}`
              }} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}
                sx={{ borderColor: alpha(t.accent.teal, 0.4), color: t.accent.teal, fontSize: '0.72rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>Select All</Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInsurances({})}
                sx={{ borderColor: alpha(t.accent.teal, 0.4), color: t.accent.teal, fontSize: '0.72rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>Deselect All</Button>
            </Box>
            <TextField fullWidth size="small" placeholder="Search insurance…" value={insuranceSearch}
              onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5, ...cellInputSx }}
              InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: t.textMuted }} /></InputAdornment> }} />
            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: `1px solid ${t.borderSubtle}`, borderRadius: 2, minHeight: 300,
              backgroundColor: t.surfaceAlt,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' }
            }}>
              {loadingInsurance
                ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: t.accent.teal }} /></Box>
                : filteredProviders.length === 0
                  ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                      <Typography variant="body2" sx={{ color: t.textMuted }}>No providers found</Typography>
                    </Box>
                  : <Box sx={{ p: 1 }}>
                      {filteredProviders.map(ins => (
                        <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', p: '6px 8px', borderRadius: 1, cursor: 'pointer', mb: 0.5,
                            backgroundColor: selectedInsurances[ins.id] ? alpha(t.accent.teal, 0.12) : 'transparent',
                            border: `1px solid ${selectedInsurances[ins.id] ? alpha(t.accent.teal, 0.3) : 'transparent'}`,
                            '&:hover': { backgroundColor: alpha(t.accent.teal, 0.08) }
                          }}>
                          <Checkbox size="small" checked={!!selectedInsurances[ins.id]}
                            onClick={e => e.stopPropagation()} onChange={() => handleInsuranceToggle(ins.id)}
                            sx={{ p: 0.5, color: t.accent.teal, '&.Mui-checked': { color: t.accent.teal } }} />
                          <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem', color: t.text }}>{ins.name}</Typography>
                          {existingStatuses.includes(ins.name) && (
                            <Chip label="Exists" size="small" sx={{
                              ml: 'auto', fontSize: '0.65rem', height: 20, fontWeight: 700,
                              backgroundColor: alpha(t.accent.success, t.isDark ? 0.18 : 0.14),
                              color: t.accent.success,
                              border: `1px solid ${alpha(t.accent.success, 0.3)}`
                            }} />
                          )}
                        </Box>
                      ))}
                    </Box>
              }
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{
        p: 2,
        borderTop: `1px solid ${t.borderSubtle}`,
        backgroundColor: t.surface
      }}>
        <Button onClick={handleClose} variant="outlined" disabled={saving || deleting}
          sx={{
            color: t.text, borderColor: t.borderStrong,
            textTransform: 'none', fontWeight: 600,
            '&:hover': { backgroundColor: t.rowHover, borderColor: t.borderStrong }
          }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── New Lab Pricing Dialog ─────────────────────────────── */
const NewLabPricingDialog = ({ open, onClose, selectedLab, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const t = useLabTokens();
  const fieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: t.fieldBg, borderRadius: 1.5 } };
  const [saving, setSaving] = useState(false);
  const [cost, setCost] = useState('');
  const [topUp, setTopUp] = useState('');
  const [uninsured, setUninsured] = useState('');
  const [drgCode, setDrgCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;
  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

useEffect(() => {
  if (open) {
    // Start with NOTHING pre-checked. Pre-ticking the already-priced
    // insurances on open made it easy to overwrite an existing
    // cost/top-up/uninsured set by accident when the user only meant to
    // add a new insurance. Updates now go through the row's Edit on the
    // main list.
    setCost(''); setTopUp(''); setUninsured(''); setDrgCode('');
    setSelectedInsurances({}); setInsuranceSearch(''); setErrors({});
  }
}, [open, insuranceProviders, existingStatuses]);


  // Guard: never let an already-priced insurance get toggled on. The UI
  // also disables those rows but this keeps state correct in any path.
  const isAlreadyPriced = (id) => {
    const provider = insuranceProviders.find(p => String(p.id) === String(id));
    return provider ? existingStatuses.includes(provider.name) : false;
  };
  const handleInsuranceToggle = (id) => {
    if (isAlreadyPriced(id)) return;
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => { const all = {}; insuranceProviders.forEach(i => { if (!existingStatuses.includes(i.name)) all[i.id] = true; }); setSelectedInsurances(all); clearError('insurance'); };
  const handleClose = () => { setCost(''); setTopUp(''); setUninsured(''); setDrgCode(''); setSelectedInsurances({}); setInsuranceSearch(''); setErrors({}); onClose(); };

  const validate = () => {
    const e = {};
    if (cost === '' || cost === null) e.cost = 'Required'; else if (isNaN(parseFloat(cost)) || parseFloat(cost) < 0) e.cost = 'Invalid';
    if (topUp === '' || topUp === null) e.topUp = 'Required'; else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0) e.topUp = 'Invalid';
    if (uninsured === '' || uninsured === null) e.uninsured = 'Required'; else if (isNaN(parseFloat(uninsured)) || parseFloat(uninsured) < 0) e.uninsured = 'Invalid';
    if (selectedCount === 0) e.insurance = 'Select at least one insurance';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Defensive — strip any already-priced insurance that somehow
      // sneaked into selection. Save only goes through for genuinely new
      // (cost, top-up, uninsured) entries.
      const rows = selectedInsuranceIds
        .filter(id => !isAlreadyPriced(id))
        .map(id => {
          const provider = insuranceProviders.find(p => String(p.id) === String(id));
          return { trans_code: selectedLab.lab, lab: selectedLab.lab, cost: parseFloat(cost), toup: parseFloat(topUp), unin: parseFloat(uninsured), G_DRG: drgCode.trim(), status: provider?.name || '' };
        });
      if (rows.length === 0) {
        await Swal.fire({ title: 'Nothing to Save', text: 'All selected insurances already have pricing for this lab. Use Edit on the row to update.', icon: 'info', confirmButtonColor: '#11998e' });
        setSaving(false);
        return;
      }
      const response = await axios.post(saveNewLab, { rows });
      if (response.data.success) {
        await Swal.fire({ title: 'Saved!', html: `<b>${selectedLab.lab}</b> priced for <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}.`, icon: 'success', timer: 2500, showConfirmButton: false, timerProgressBar: true });
        if (onSaved) onSaved(); handleClose();
      } else throw new Error(response.data.message || 'Save failed');
    } catch (err) {
      await Swal.fire({ title: 'Save Failed', text: err.response?.data?.message || err.message, icon: 'error' });
    } finally { setSaving(false); }
  };

  const filteredProviders = insuranceProviders.filter(i => i.name?.toLowerCase().includes(insuranceSearch.toLowerCase()));
  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssigned = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!selectedLab) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: {
        borderRadius: 3, overflow: 'hidden',
        backgroundColor: t.pageBg, backgroundImage: 'none',
        border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
        boxShadow: t.isDark ? '0 24px 64px rgba(0,0,0,0.7)' : '0 24px 64px rgba(15,23,42,0.18)',
      } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.78)} 100%)`,
        color: 'white', p: 2.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'relative',
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            backgroundColor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}>
            <LabIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>New Insurance Pricing</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{selectedLab.lab}</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.18)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
          position: 'relative', zIndex: 1
        }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, backgroundColor: t.pageBg }}>

        <Grid container sx={{ minHeight: 440 }} columns={{ xs: 4, sm: 8, md: 12 }}>

          <Grid item xs={12} md={5} size={6} sx={{
            p: 3,
            borderRight: { md: `1px solid ${t.borderSubtle}` },
            backgroundColor: t.surfaceAlt
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.teal, boxShadow: `0 0 10px ${alpha(t.accent.teal, 0.5)}` }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.74rem' }}>Set Pricing</Typography>
            </Box>
            <TextField fullWidth size="small" label="Tariff  GH¢ *" type="number" value={cost} onChange={e => { setCost(e.target.value); clearError('cost'); }} error={!!errors.cost} helperText={errors.cost} inputProps={{ min: 0, step: '0.01' }} sx={{ mb: 2, ...fieldSx }} />
            <TextField fullWidth size="small" label="Top-up GH¢ *" type="number" value={topUp} onChange={e => { setTopUp(e.target.value); clearError('topUp'); }} error={!!errors.topUp} helperText={errors.topUp} inputProps={{ min: 0, step: '0.01' }} sx={{ mb: 2, ...fieldSx }} />
            <TextField fullWidth size="small" label="Uninsured GH¢ *" type="number" value={uninsured} onChange={e => { setUninsured(e.target.value); clearError('uninsured'); }} error={!!errors.uninsured} helperText={errors.uninsured} inputProps={{ min: 0, step: '0.01' }} sx={{ mb: 2, ...fieldSx }} />
            <Divider sx={{ my: 1.5, borderColor: t.borderSubtle }} />
            <TextField fullWidth size="small" label="DRG Code (optional)" value={drgCode} onChange={e => setDrgCode(e.target.value)} sx={{ mb: 2, ...fieldSx }} placeholder="e.g., INVE122" />
            <Box sx={{
              mt: 1, p: 1.5, borderRadius: 1.5,
              backgroundColor: alpha(t.accent.info, t.isDark ? 0.12 : 0.08),
              border: `1px solid ${alpha(t.accent.info, 0.25)}`,
              fontSize: '0.72rem', color: t.text, lineHeight: 1.6
            }}>
              <Box component="span" sx={{ fontWeight: 700, color: t.accent.info }}>Tariff:</Box> Base insurance tariff<br />
              <Box component="span" sx={{ fontWeight: 700, color: t.accent.info }}>Top-up:</Box> Additional patient fee<br />
              <Box component="span" sx={{ fontWeight: 700, color: t.accent.info }}>Uninsured:</Box> Non-insured price
            </Box>
          </Grid>


          <Grid item xs={12} md={7} size={6} sx={{ p: 3, display: 'flex', flexDirection: 'column', backgroundColor: t.surface }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: errors.insurance ? t.accent.danger : t.accent.primary, boxShadow: `0 0 10px ${alpha(errors.insurance ? t.accent.danger : t.accent.primary, 0.5)}` }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: errors.insurance ? t.accent.danger : t.text, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.74rem' }}>Select Insurance Providers *</Typography>
                  <Typography variant="caption" sx={{ color: errors.insurance ? t.accent.danger : t.textMuted }}>{selectedCount} selected | {availableProviders.length} available</Typography>
                </Box>
              </Box>
              {selectedCount > 0 && <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: `${t.accent.success} !important` }} />} label={`${selectedCount}`} sx={{
                backgroundColor: alpha(t.accent.success, t.isDark ? 0.18 : 0.14),
                color: t.accent.success,
                fontWeight: 700, fontSize: '0.7rem',
                border: `1px solid ${alpha(t.accent.success, 0.3)}`
              }} />}
            </Box>
            {errors.insurance && <Typography variant="caption" sx={{ color: t.accent.danger, fontSize: '0.7rem', mb: 0.5 }}>{errors.insurance}</Typography>}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderColor: alpha(t.accent.teal, 0.4), color: t.accent.teal, fontSize: '0.72rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>Select All Available</Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInsurances({})} sx={{ borderColor: alpha(t.accent.teal, 0.4), color: t.accent.teal, fontSize: '0.72rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}>Deselect All</Button>
            </Box>
            <TextField fullWidth size="small" placeholder="Search insurance…" value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5, ...fieldSx }}
              InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: t.textMuted }} /></InputAdornment> }} />
            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: `1px solid ${errors.insurance ? t.accent.danger : t.borderSubtle}`,
              borderRadius: 2, p: 1, minHeight: 200,
              backgroundColor: t.surfaceAlt,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' }
            }}>
              {loadingInsurance ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: t.accent.teal }} /></Box> : (
                <>
                  {availableProviders.length > 0 && <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: t.accent.success, display: 'block', mb: 0.5, px: 0.5, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>Available ({availableProviders.length})</Typography>
                    {availableProviders.map(ins => (
                      <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)} sx={{
                        display: 'flex', alignItems: 'center', p: '6px 8px', borderRadius: 1, cursor: 'pointer', mb: 0.5,
                        backgroundColor: selectedInsurances[ins.id] ? alpha(t.accent.teal, 0.12) : 'transparent',
                        border: `1px solid ${selectedInsurances[ins.id] ? alpha(t.accent.teal, 0.3) : 'transparent'}`,
                        '&:hover': { backgroundColor: alpha(t.accent.teal, 0.08) }
                      }}>
                        <Checkbox size="small" checked={!!selectedInsurances[ins.id]} onClick={e => e.stopPropagation()} onChange={() => handleInsuranceToggle(ins.id)} sx={{ p: 0.5, color: t.accent.teal, '&.Mui-checked': { color: t.accent.teal } }} />
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem', color: t.text }}>{ins.name}</Typography>
                      </Box>
                    ))}
                  </Box>}

                  {alreadyAssigned.length > 0 && <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${t.borderSubtle}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: t.accent.danger, display: 'block', mb: 0.5, px: 0.5, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                      Already Priced ({alreadyAssigned.length}) — locked, edit from the main list
                    </Typography>
                    {alreadyAssigned.map(ins => (
                      <Box key={ins.id}
                        sx={{
                          display: 'flex', alignItems: 'center', p: '6px 8px', borderRadius: 1,
                          cursor: 'not-allowed', mb: 0.5, opacity: 0.6,
                          backgroundColor: 'transparent',
                          border: `1px dashed ${alpha(t.accent.danger, 0.25)}`,
                        }}
                        title="This insurance already has pricing — use Edit on the row to update it."
                      >
                        <Checkbox size="small"
                          checked={false}
                          disabled
                          sx={{ p: 0.5, color: t.accent.danger, '&.Mui-disabled': { color: alpha(t.accent.danger, 0.45) } }} />
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem', color: t.textMuted, textDecoration: 'line-through' }}>{ins.name}</Typography>
                        <Chip label="Already priced" size="small" sx={{
                          ml: 'auto', fontSize: '0.65rem', height: 20, fontWeight: 700,
                          backgroundColor: alpha(t.accent.danger, t.isDark ? 0.18 : 0.14),
                          color: t.accent.danger,
                          border: `1px solid ${alpha(t.accent.danger, 0.3)}`
                        }} />
                      </Box>
                    ))}
                  </Box>}

                  {availableProviders.length === 0 && alreadyAssigned.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}><Typography variant="body2" sx={{ color: t.textMuted }}>No providers found</Typography></Box>}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{
        p: 2,
        borderTop: `1px solid ${t.borderSubtle}`,
        backgroundColor: t.surface,
        gap: 1
      }}>
        <Button onClick={handleClose} variant="outlined" disabled={saving}
          sx={{
            color: t.text, borderColor: t.borderStrong, textTransform: 'none', fontWeight: 600,
            '&:hover': { backgroundColor: t.rowHover, borderColor: t.borderStrong }
          }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{
            background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
            color: 'white', fontWeight: 700, px: 3, textTransform: 'none',
            boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.35)}`,
            '&:hover': { boxShadow: `0 8px 22px ${alpha(t.accent.teal, 0.45)}` },
            '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
          }}>
          {saving ? 'Saving…' : 'Save Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Main Component ─────────────────────────────────────── */
const Laboratory = () => {
  const t = useLabTokens();
  const fieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: t.fieldBg, borderRadius: 1.5 } };
  const headerGradient = `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.78)} 100%)`;
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [labGroups, setLabGroups] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const [insuranceRows, setInsuranceRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [newPricingDialogOpen, setNewPricingDialogOpen] = useState(false);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);


  // ── Move lab tests state ──────────────────────────────────
  const [selectMode, setSelectMode]                     = useState(false);
  const [selectedLabNames, setSelectedLabNames]         = useState(new Set());
  const [moveDialogOpen, setMoveDialogOpen]             = useState(false);
  const [moveTargetInsurance, setMoveTargetInsurance]   = useState('');
  const [moving, setMoving]                             = useState(false);

  const [insuranceFilter, setInsuranceFilter] = useState('');

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try { const res = await axios.get(loadInsurance); if (res.data.success) setInsuranceProviders(res.data.results); else setError('Failed to load insurance'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load insurance'); }
    finally { setLoadingInsurance(false); }
  };

  const loadLabGroups = async () => {
    setLoadingLabs(true); setError(null);
    try { const res = await axios.get(loadLabCategoriesbd); if (res.data.success) setLabGroups(res.data.results); else setError('Failed to load lab tests'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load lab tests'); }
    finally { setLoadingLabs(false); }
  };

  const loadInsuranceRecords = async (lab) => {
    setLoadingRecords(true); setInsuranceRows([]); setSelectedRowIds([]);
    try { const res = await axios.post(getLabInsurances, { categoryName: lab.lab }); if (res.data.success) setInsuranceRows(res.data.results); }
    catch (err) { await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load records', icon: 'error' }); }
    finally { setLoadingRecords(false); }
  };

  useEffect(() => { loadInsuranceProviders(); loadLabGroups(); }, []);

  const handleLabSelect = async (lab) => {
    if (selectedLab?.lab === lab.lab) { setSelectedLab(null); setInsuranceRows([]); setSelectedRowIds([]); return; }
    setSelectedLab(lab);
    await loadInsuranceRecords(lab);
  };

  const handleRowChange = (idx, field, value) => setInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const handleUpdateRow = async (row) => {
    if (row.cost === '' || row.toup === '' || row.unin === '') {
       await Swal.fire({ title: 'Validation', text: 'Fill in all price fields', icon: 'warning' }); return; }
    if (!row.trans_code) { await Swal.fire({ title: 'Error', text: 'Transaction code missing', icon: 'error' }); return; }
    const rowId = `${row.trans_code}-${row.status}`;
    setUpdatingRowId(rowId);
    try {
      const res = await axios.post(updateInsuredLab, { trans_code: row.trans_code, lab: row.lab, status: row.status, cost: parseFloat(row.cost), toup: parseFloat(row.toup), unin: parseFloat(row.unin), G_DRG: row.G_DRG || '' });
      if (res.data.success) await Swal.fire({ title: 'Updated!', text: `${row.lab} — ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
      else throw new Error(res.data.message || 'Update failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.trans_code) return;
    const result = await Swal.fire({ title: 'Delete?', html: `Delete pricing for <strong>${row.status}</strong>?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e', confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    const rowId = `${row.trans_code}-${row.status}`;
    setDeletingRowId(rowId);
    try {
      const res = await axios.post(deleteInsuredLab, { trans_code: row.trans_code, lab: row.lab, status: row.status });
      if (res.data.success) { await Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false }); setInsuranceRows(prev => prev.filter(r => !(r.trans_code === row.trans_code && r.status === row.status))); setSelectedRowIds(prev => prev.filter(id => id !== rowId)); }
      else throw new Error(res.data.message || 'Delete failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setDeletingRowId(null); }
  };

  const getRowId = (row) => `${row.trans_code}-${row.status}`;
  const handleRowSelectionToggle = (id) => setSelectedRowIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSelectAllRows = () => { if (selectedRowIds.length === insuranceRows.length) setSelectedRowIds([]); else setSelectedRowIds(insuranceRows.map(r => getRowId(r))); };

 const handleBulkApply = () => {
  if (selectedRowIds.length === 0) {
    Swal.fire({ title: 'No Selection', text: 'Select at least one row.', icon: 'info', confirmButtonColor: '#11998e' });
    return;
  }
  const sourceRow = insuranceRows.find(r => getRowId(r) === selectedRowIds[0]);
  if (!sourceRow) return;
  setSourceRowForBulkApply(sourceRow);
  setBulkApplyDialogOpen(true);
};

const filteredLabs = labGroups
  .filter((l, idx, arr) => arr.findIndex(x => x.lab === l.lab) === idx)
  .filter(l => l.lab?.toLowerCase().includes(searchTerm.toLowerCase()))
  .filter(l => !insuranceFilter || l.insurances?.some(i => i.name === insuranceFilter));

const existingStatuses = insuranceRows.map(r => r.status);


  const toggleLabSelection = (labName) => {
  setSelectedLabNames(prev => {
    const next = new Set(prev);
    next.has(labName) ? next.delete(labName) : next.add(labName);
    return next;
  });
};

const handleSelectAllVisibleLabs = (visibleLabs) => {
  setSelectedLabNames(new Set(visibleLabs.map(l => l.lab)));
};

const handleDeselectAllLabs = () => setSelectedLabNames(new Set());

const handleMoveLabs = async () => {
  if (selectedLabNames.size === 0 || !moveTargetInsurance) return;
  setMoving(true);
  try {
    const res = await axios.post(moveLabToInsurancedb, {
      labNames:        Array.from(selectedLabNames),
      targetInsurance: moveTargetInsurance,
    });
    if (res.data.success) {
      await Swal.fire({
        title: 'Done!',
        text: res.data.message,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        timerProgressBar: true,
      });
      setMoveDialogOpen(false);
      setSelectedLabNames(new Set());
      setMoveTargetInsurance('');
      setSelectMode(false);
      loadLabGroups();
    } else {
      throw new Error(res.data.message);
    }
  } catch (err) {
    await Swal.fire({ title: 'Move Failed', text: err.response?.data?.message || err.message, icon: 'error' });
  } finally {
    setMoving(false);
  }
};

  return (
    <>
      <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, backgroundColor: t.pageBg, minHeight: '100%' }}>
        <Paper elevation={0} sx={{
          margin: '0 auto', borderRadius: 3, overflow: 'hidden',
          backgroundColor: t.surface, backgroundImage: 'none',
          border: `1px solid ${t.borderSubtle}`,
          boxShadow: t.cardShadow
        }}>
          <Box sx={{
            background: headerGradient,
            color: 'white', p: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 1.5,
            position: 'relative',
            '&::after': {
              content: '""', position: 'absolute', inset: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
              pointerEvents: 'none'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, position: 'relative', zIndex: 1 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 1.75,
                backgroundColor: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)'
              }}>
                <LabIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.1 }}>Laboratory Tariff Management</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>Configure lab tests and per-insurance pricing.</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, position: 'relative', zIndex: 1 }}>
              <Button variant="contained" onClick={() => { loadLabGroups(); loadInsuranceProviders(); }} disabled={loadingLabs}
                startIcon={loadingLabs ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                size="small" sx={{
                  background: 'rgba(255,255,255,0.16)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  color: 'white', textTransform: 'none', fontWeight: 600,
                  '&:hover': { background: 'rgba(255,255,255,0.28)' }
                }}>
                {loadingLabs ? 'Loading…' : 'Reload'}
              </Button>
              <Tooltip title={selectedLab ? 'Add insurance pricing' : 'Select a lab test first'}>
                <span>
                  <Button variant="contained" startIcon={<AddIcon />} size="small"
                    onClick={() => { if (!selectedLab) { Swal.fire({ title: 'Select a Lab Test', text: 'Click a lab test on the left first.', icon: 'info', confirmButtonColor: t.accent.teal }); return; } setNewPricingDialogOpen(true); }}
                    sx={{
                      background: '#FFFFFF',
                      color: t.accent.teal,
                      fontWeight: 700, textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                      '&:hover': { background: '#F8FAFC', boxShadow: '0 6px 18px rgba(0,0,0,0.2)' }
                    }}>
                    New Pricing
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ p: 3, backgroundColor: t.surface }}>
            {error && (
              <Box sx={{
                mb: 3, p: 1.5, borderRadius: 1.5,
                backgroundColor: alpha(t.accent.danger, t.isDark ? 0.12 : 0.08),
                border: `1px solid ${alpha(t.accent.danger, 0.3)}`,
                color: t.accent.danger,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <Typography sx={{ fontSize: '0.88rem' }}>{error}</Typography>
                <IconButton size="small" onClick={() => setError(null)} sx={{ color: t.accent.danger }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            {/* LEFT: Lab list from lab_prices */}
            <Grid item xs={12} md={4} size={4}>
              <Paper elevation={0} sx={{
                p: 2.5,
                border: `1px solid ${t.borderSubtle}`,
                borderRadius: 2,
                backgroundColor: t.surfaceAlt,
                backgroundImage: 'none',
                boxShadow: t.cardShadow,
                height: 600,
                display: 'flex', flexDirection: 'column'
              }}>

                {/* Insurance filter */}
                <Box sx={{ mb: insuranceFilter ? 1 : 1.5 }}>
                  <select
                    value={insuranceFilter}
                    onChange={e => setInsuranceFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      fontSize: '0.82rem',
                      borderRadius: '6px',
                      border: `1px solid ${insuranceFilter ? t.accent.teal : t.borderStrong}`,
                      outline: 'none',
                      color: insuranceFilter ? t.accent.teal : t.textMuted,
                      fontWeight: insuranceFilter ? 600 : 400,
                      background: insuranceFilter ? alpha(t.accent.teal, 0.06) : t.fieldBg,
                      cursor: 'pointer',
                      appearance: 'auto',
                    }}
                  >
                    <option value="">All Insurances</option>
                    {insuranceProviders.map(ins => (
                      <option key={ins.id} value={ins.name}>{ins.name}</option>
                    ))}
                  </select>
                </Box>

                {/* Active filter chip */}
                {insuranceFilter && (
                  <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip size="small"
                      icon={<InsuranceIcon sx={{ fontSize: '13px !important', color: `${t.accent.teal} !important` }} />}
                      label={insuranceFilter}
                      onDelete={() => setInsuranceFilter('')}
                      sx={{
                        fontSize: '0.72rem',
                        backgroundColor: alpha(t.accent.teal, t.isDark ? 0.18 : 0.12),
                        color: t.accent.teal,
                        border: `1px solid ${alpha(t.accent.teal, 0.3)}`,
                        fontWeight: 700, height: 22
                      }} />
                    <Typography variant="caption" sx={{ color: t.textMuted, fontSize: '0.68rem' }}>
                      — filtering labs
                    </Typography>
                  </Box>
                )}

                {/* Search */}
                <TextField fullWidth size="small" placeholder="Search lab tests…" value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 2, ...fieldSx }}
                  InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: t.accent.teal }} /></InputAdornment> }} />

                {/* Header row with Move toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 1, backgroundColor: t.accent.teal, boxShadow: `0 0 10px ${alpha(t.accent.teal, 0.5)}` }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Available Lab Tests
                    </Typography>
                    <Chip size="small" label={`${filteredLabs.length} total`} sx={{
                      fontSize: '0.68rem', fontWeight: 700,
                      backgroundColor: alpha(t.accent.primary, t.isDark ? 0.18 : 0.12),
                      color: t.accent.primary,
                      border: `1px solid ${alpha(t.accent.primary, 0.3)}`
                    }} />
                  </Box>
                  <Tooltip title={selectMode ? 'Exit selection mode' : 'Select tests to copy to another insurance'}>
                    <Button size="small" variant={selectMode ? 'contained' : 'outlined'}
                      startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setSelectMode(s => !s); setSelectedLabNames(new Set()); }}
                      sx={{
                        fontSize: '0.68rem', py: 0.3, px: 1, textTransform: 'none', fontWeight: 600,
                        borderColor: alpha(t.accent.teal, 0.4),
                        color: selectMode ? 'white' : t.accent.teal,
                        background: selectMode
                          ? `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`
                          : 'transparent',
                        '&:hover': {
                          background: selectMode
                            ? `linear-gradient(135deg, ${alpha(t.accent.teal, 0.92)} 0%, ${alpha(t.accent.teal, 0.72)} 100%)`
                            : alpha(t.accent.teal, 0.08)
                        }
                      }}>
                      {selectMode ? 'Cancel' : 'Move'}
                    </Button>
                  </Tooltip>
                </Box>

                {/* Selection action bar */}
                {selectMode && (
                  <Box sx={{
                    mb: 1, p: 1, borderRadius: 1.5,
                    backgroundColor: alpha(t.accent.teal, t.isDark ? 0.12 : 0.06),
                    border: `1px solid ${alpha(t.accent.teal, 0.25)}`,
                    display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: t.accent.teal, fontSize: '0.72rem' }}>
                      {selectedLabNames.size} selected
                    </Typography>
                    <Button size="small" variant="text"
                      onClick={() => handleSelectAllVisibleLabs(filteredLabs)}
                      sx={{ fontSize: '0.68rem', py: 0.2, color: t.accent.teal, minWidth: 0, textTransform: 'none', fontWeight: 600 }}>
                      All
                    </Button>
                    <Button size="small" variant="text" onClick={handleDeselectAllLabs}
                      sx={{ fontSize: '0.68rem', py: 0.2, color: t.textMuted, minWidth: 0, textTransform: 'none' }}>
                      None
                    </Button>
                    {selectedLabNames.size > 0 && (
                      <Button size="small" variant="contained"
                        startIcon={<SwapHorizIcon sx={{ fontSize: 13 }} />}
                        onClick={() => setMoveDialogOpen(true)}
                        sx={{
                          ml: 'auto', fontSize: '0.68rem', py: 0.3, px: 1.2, textTransform: 'none', fontWeight: 700,
                          background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                          boxShadow: `0 3px 10px ${alpha(t.accent.teal, 0.28)}`,
                          '&:hover': { boxShadow: `0 5px 14px ${alpha(t.accent.teal, 0.4)}` }
                        }}>
                        Move {selectedLabNames.size}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Selected lab indicator (only in normal mode) */}
                {selectedLab && !selectMode && (
                  <Box sx={{
                    mb: 1.5, p: 1.25, borderRadius: 1.5,
                    backgroundColor: alpha(t.accent.teal, t.isDark ? 0.12 : 0.08),
                    border: `1px solid ${alpha(t.accent.teal, 0.3)}`,
                    display: 'flex', alignItems: 'center', gap: 1
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: t.accent.teal }} />
                    <Typography variant="caption" sx={{ color: t.text, fontWeight: 600, fontSize: '0.78rem', flex: 1 }} noWrap>
                      Selected: {selectedLab.lab}
                    </Typography>
                    <IconButton size="small" onClick={() => { setSelectedLab(null); setInsuranceRows([]); setSelectedRowIds([]); }}
                      sx={{ p: 0.25, color: t.textMuted }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}

                {/* Lab list */}
                <Box sx={{
                  flex: 1, overflowY: 'auto', pr: 0.5,
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' }
                }}>
                  {loadingLabs
                    ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress sx={{ color: t.accent.teal }} />
                      </Box>
                    : filteredLabs.length === 0
                      ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
                          <ScienceIcon sx={{ fontSize: 40, color: t.textDim }} />
                          <Typography variant="body2" sx={{ color: t.textMuted }}>No lab tests found</Typography>
                        </Box>
                      : filteredLabs.map((lab, idx) => {
                          const isSelected = selectedLab?.lab === lab.lab && !selectMode;
                          const isChecked  = selectedLabNames.has(lab.lab);
                          const isActive   = isSelected || isChecked;
                          return (
                            <Box key={`${lab.lab}-${idx}`}
                              onClick={() => selectMode ? toggleLabSelection(lab.lab) : handleLabSelect(lab)}
                              sx={{
                                display: 'flex', alignItems: 'center', mb: 0.5, p: 1.1, borderRadius: 1.5,
                                cursor: 'pointer', transition: 'all 0.18s ease',
                                border: `1.5px solid ${
                                  isSelected ? alpha(t.accent.teal, 0.6)
                                  : isChecked ? alpha(t.accent.success, 0.5)
                                  : 'transparent'
                                }`,
                                backgroundColor: isSelected ? alpha(t.accent.teal, 0.12)
                                  : isChecked ? alpha(t.accent.success, 0.10)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: alpha(t.accent.teal, 0.08),
                                  transform: 'translateX(3px)',
                                  borderColor: alpha(t.accent.teal, 0.3)
                                }
                              }}>
                              {selectMode ? (
                                <Checkbox size="small" checked={isChecked}
                                  onChange={() => toggleLabSelection(lab.lab)}
                                  onClick={e => e.stopPropagation()}
                                  sx={{ p: 0.3, color: t.accent.teal, '&.Mui-checked': { color: t.accent.teal }, mr: 0.5 }} />
                              ) : (
                                <ScienceIcon sx={{ fontSize: 16, mr: 1, color: isSelected ? t.accent.teal : t.textDim }} />
                              )}
                              <Typography variant="body2" noWrap sx={{
                                fontSize: '0.85rem', flex: 1,
                                color: isActive ? t.text : t.textMuted,
                                fontWeight: isActive ? 700 : 500
                              }}>
                                {lab.lab}
                              </Typography>
                              {isChecked && selectMode && (
                                <CheckCircleIcon sx={{ fontSize: 14, color: t.accent.success, ml: 'auto' }} />
                              )}
                            </Box>
                          );
                        })
                  }
                </Box>
              </Paper>
            </Grid>

            {/* RIGHT: Insurance records */}
            <Grid item xs={12} md={8} size={8}>
              <Paper elevation={0} sx={{
                border: `1px solid ${t.borderSubtle}`,
                borderRadius: 2,
                backgroundColor: t.surface,
                backgroundImage: 'none',
                boxShadow: t.cardShadow,
                height: 600,
                display: 'flex', flexDirection: 'column', overflow: 'hidden'
              }}>
                <Box sx={{
                  px: 2.5, py: 1.75,
                  borderBottom: `1px solid ${t.borderSubtle}`,
                  backgroundColor: t.surfaceAlt,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexShrink: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 3, height: 22, borderRadius: 1, backgroundColor: t.accent.primary, boxShadow: `0 0 12px ${alpha(t.accent.primary, 0.5)}` }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: t.text, fontSize: '0.88rem' }}>Insurance Coverage Records</Typography>
                      <Typography variant="caption" sx={{ color: t.textMuted }}>{selectedLab ? `Records for: ${selectedLab.lab}` : 'Select a lab test to load records'}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && <Chip size="small" label={`${selectedRowIds.length} selected`} sx={{
                      backgroundColor: alpha(t.accent.primary, t.isDark ? 0.18 : 0.12),
                      color: t.accent.primary,
                      fontWeight: 700, fontSize: '0.7rem',
                      border: `1px solid ${alpha(t.accent.primary, 0.3)}`
                    }} />}
                    {selectedLab && !loadingRecords && (() => {
                      const hasRecs = insuranceRows.length > 0;
                      const c = hasRecs ? t.accent.success : t.accent.danger;
                      return (
                        <Chip
                          icon={<InsuranceIcon sx={{ fontSize: '14px !important', color: `${c} !important` }} />}
                          label={`${insuranceRows.length} record${insuranceRows.length !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            backgroundColor: alpha(c, t.isDark ? 0.18 : 0.14),
                            color: c,
                            fontWeight: 700, fontSize: '0.7rem',
                            border: `1px solid ${alpha(c, 0.3)}`
                          }}
                        />
                      );
                    })()}
                  </Box>
                </Box>

                {selectedLab && insuranceRows.length > 0 && (
                  <Box sx={{
                    px: 2.5, py: 1,
                    borderBottom: `1px solid ${t.borderSubtle}`,
                    backgroundColor: t.surface,
                    display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0
                  }}>
                    <Button size="small" variant="outlined" onClick={handleSelectAllRows}
                      sx={{ fontSize: '0.72rem', py: 0.4, textTransform: 'none', fontWeight: 600, borderColor: alpha(t.accent.teal, 0.4), color: t.accent.teal }}>
                      {selectedRowIds.length === insuranceRows.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button size="small" variant="contained" startIcon={<CopyIcon />} onClick={handleBulkApply}
                      disabled={selectedRowIds.length === 0}
                      sx={{
                        fontSize: '0.72rem', py: 0.4, fontWeight: 700, textTransform: 'none',
                        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.3)}`,
                        '&:hover': { boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.4)}` },
                        '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
                      }}>
                      Apply to Insurances
                    </Button>
                    <Typography variant="caption" sx={{ color: t.textMuted, fontSize: '0.7rem', fontStyle: 'italic' }}>
                      {selectedRowIds.length === 0 && 'Select rows to apply pricing to multiple insurances'}
                      {selectedRowIds.length >= 1 && `${selectedRowIds.length} row${selectedRowIds.length !== 1 ? 's' : ''} selected — click "Apply to Insurances"`}
                    </Typography>
                  </Box>
                )}

                {!selectedLab ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: 2,
                      backgroundColor: alpha(t.accent.primary, 0.12),
                      color: t.accent.primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <InsuranceIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: t.textMuted }} textAlign="center">
                      Click any lab test on the left<br />to view its insurance pricing
                    </Typography>
                  </Box>
                ) : loadingRecords ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center">
                      <CircularProgress size={36} sx={{ color: t.accent.teal }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: t.textMuted }}>Loading…</Typography>
                    </Box>
                  </Box>
                ) : insuranceRows.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2,
                      backgroundColor: alpha(t.accent.danger, 0.12),
                      color: t.accent.danger,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <InsuranceIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: t.textMuted }}>No insurance pricing found.</Typography>
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setNewPricingDialogOpen(true)}
                      sx={{
                        mt: 1, textTransform: 'none', fontWeight: 700,
                        background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.3)}`,
                        '&:hover': { boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.4)}` }
                      }}>
                      Add Insurance Pricing
                    </Button>
                  </Box>
                ) : (
                  <TableContainer sx={{
                    flex: 1, overflowY: 'auto', overflowX: 'auto',
                    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                    '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' }
                  }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 950 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{
                            background: headerGradient, color: '#fff',
                            width: 48, textAlign: 'center', py: 1.2, px: 0.5, borderBottom: 'none'
                          }}>
                            <Checkbox size="small" checked={selectedRowIds.length === insuranceRows.length && insuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < insuranceRows.length}
                              onChange={handleSelectAllRows} sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' }, p: 0.5 }} />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Uninsured', 'DRG Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{
                              background: headerGradient, color: '#fff',
                              fontWeight: 700, fontSize: '0.72rem',
                              letterSpacing: '0.4px', textTransform: 'uppercase',
                              borderBottom: 'none', py: 1.2, whiteSpace: 'nowrap',
                              ...(label === '#' && { width: 36, textAlign: 'center' }),
                              ...(label === 'Actions' && { width: 180, textAlign: 'center' })
                            }}>{label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {insuranceRows.map((row, idx) => {
                          const rowId = getRowId(row);
                          return (
                            <EditableRow key={`${rowId}-${idx}`} row={row} idx={idx}
                              selected={selectedRowIds.includes(rowId)} onSelectToggle={handleRowSelectionToggle}
                              onChange={(field, value) => handleRowChange(idx, field, value)}
                              onUpdate={handleUpdateRow} onDelete={handleDeleteRow}
                              updating={updatingRowId === rowId} deleting={deletingRowId === rowId} />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      </Box>



      {/* ══════════ Move Lab Tests Dialog ══════════ */}
      <Dialog open={moveDialogOpen} onClose={() => !moving && setMoveDialogOpen(false)}
        maxWidth="xs" fullWidth TransitionComponent={Fade}
        PaperProps={{ sx: {
          borderRadius: 3, overflow: 'hidden',
          backgroundColor: t.pageBg, backgroundImage: 'none',
          border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
          boxShadow: t.isDark ? '0 24px 64px rgba(0,0,0,0.7)' : '0 24px 64px rgba(15,23,42,0.18)',
        } }}>

        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: headerGradient,
            color: 'white', px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'relative',
            '&::after': {
              content: '""', position: 'absolute', inset: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 60%)',
              pointerEvents: 'none'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 1.5,
                backgroundColor: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)'
              }}>
                <SwapHorizIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>Copy Lab Tests</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {selectedLabNames.size} test{selectedLabNames.size !== 1 ? 's' : ''} selected
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setMoveDialogOpen(false)} disabled={moving} sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.18)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
              position: 'relative', zIndex: 1
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1, backgroundColor: t.pageBg }}>

          {/* Selected labs chips */}
          <Box sx={{
            mb: 2.5, p: 1.5, borderRadius: 2,
            backgroundColor: alpha(t.accent.teal, t.isDark ? 0.10 : 0.06),
            border: `1px solid ${alpha(t.accent.teal, 0.25)}`,
            maxHeight: 130, overflowY: 'auto'
          }}>
            <Typography variant="caption" sx={{
              fontWeight: 700, color: t.accent.teal,
              textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem',
              display: 'block', mb: 1
            }}>
              Lab tests to copy:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Array.from(selectedLabNames).map(name => (
                <Chip key={name} label={name} size="small"
                  sx={{
                    fontSize: '0.72rem', height: 22, fontWeight: 700,
                    backgroundColor: alpha(t.accent.teal, t.isDark ? 0.20 : 0.14),
                    color: t.accent.teal,
                    border: `1px solid ${alpha(t.accent.teal, 0.3)}`
                  }} />
              ))}
            </Box>
          </Box>

          {/* Target insurance picker */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: t.text, display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>
            Copy to Insurance *
          </Typography>
          <Box sx={{
            border: `1px solid ${moveTargetInsurance ? alpha(t.accent.teal, 0.4) : t.borderSubtle}`,
            borderRadius: 1.5, overflow: 'hidden', maxHeight: 220, overflowY: 'auto',
            backgroundColor: t.surface,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: alpha(t.text, 0.15), borderRadius: '10px' }
          }}>
            {insuranceProviders.map(ins => {
              const isSel = moveTargetInsurance === ins.name;
              return (
                <Box key={ins.id}
                  onClick={() => setMoveTargetInsurance(ins.name)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 2, py: 1,
                    cursor: 'pointer', transition: 'all 0.15s',
                    backgroundColor: isSel ? alpha(t.accent.teal, 0.12) : 'transparent',
                    borderLeft: isSel ? `3px solid ${t.accent.teal}` : '3px solid transparent',
                    '&:hover': { backgroundColor: alpha(t.accent.teal, 0.08) }
                  }}>
                  <InsuranceIcon sx={{ fontSize: 15, color: isSel ? t.accent.teal : t.textDim, mr: 1 }} />
                  <Typography variant="body2" sx={{
                    fontSize: '0.85rem',
                    fontWeight: isSel ? 700 : 500,
                    color: isSel ? t.text : t.textMuted
                  }}>
                    {ins.name}
                  </Typography>
                  {isSel && <CheckCircleIcon sx={{ fontSize: 14, color: t.accent.teal, ml: 'auto' }} />}
                </Box>
              );
            })}
          </Box>

          <Box sx={{
            mt: 2, p: 1.25, borderRadius: 1.5,
            backgroundColor: alpha(t.accent.info, t.isDark ? 0.12 : 0.08),
            border: `1px solid ${alpha(t.accent.info, 0.25)}`,
            fontSize: '0.78rem', color: t.text, lineHeight: 1.5
          }}>
            Tests already assigned to the target insurance will be skipped automatically.
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3, py: 2, gap: 1,
          borderTop: `1px solid ${t.borderSubtle}`,
          backgroundColor: t.surface
        }}>
          <Button onClick={() => { setMoveDialogOpen(false); setMoveTargetInsurance(''); }}
            disabled={moving} variant="outlined"
            sx={{
              color: t.text, borderColor: t.borderStrong, flex: 1,
              textTransform: 'none', fontWeight: 600,
              '&:hover': { backgroundColor: t.rowHover, borderColor: t.borderStrong }
            }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMoveLabs}
            disabled={moving || !moveTargetInsurance}
            startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
            sx={{
              flex: 1, fontWeight: 700, textTransform: 'none',
              background: `linear-gradient(135deg, ${t.accent.teal} 0%, ${alpha(t.accent.teal, 0.82)} 100%)`,
              boxShadow: `0 4px 12px ${alpha(t.accent.teal, 0.3)}`,
              '&:hover': { boxShadow: `0 6px 16px ${alpha(t.accent.teal, 0.4)}` },
              '&:disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
            }}>
            {moving ? 'Copying…' : `Copy ${selectedLabNames.size}`}
          </Button>
        </DialogActions>
      </Dialog>

      <NewLabPricingDialog open={newPricingDialogOpen} onClose={() => setNewPricingDialogOpen(false)}
        selectedLab={selectedLab} insuranceProviders={insuranceProviders} loadingInsurance={loadingInsurance}
        onSaved={() => { if (selectedLab) loadInsuranceRecords(selectedLab); }} existingStatuses={existingStatuses} />

      <BulkApplyDialog
  open={bulkApplyDialogOpen}
  onClose={() => { setBulkApplyDialogOpen(false); setSourceRowForBulkApply(null); }}
  sourceRow={sourceRowForBulkApply}
  selectedRows={insuranceRows.filter(r => selectedRowIds.includes(getRowId(r)))}
  insuranceProviders={insuranceProviders}
  loadingInsurance={loadingInsurance}
  onSaved={() => { if (selectedLab) loadInsuranceRecords(selectedLab); }}
  existingStatuses={existingStatuses}
/>
    </>
  );
};

export default Laboratory;